/**
 * Read-Only Mode Hook
 * 
 * Manages read-only mode state for the application. When enabled, this mode
 * restricts users from performing write operations (create, update, delete)
 * while still allowing read access to view data.
 * 
 * **Key Features:**
 * - Checks if read-only mode is enabled via feature flags
 * - Admin users automatically bypass read-only restrictions
 * - Organization-level and user-level read-only control
 * - Loading state management for async flag checks
 * 
 * **Use Cases:**
 * - Maintenance windows where data modifications should be prevented
 * - System upgrades requiring temporary read-only access
 * - Demo/sandbox environments
 * - Compliance or audit periods
 * 
 * @returns {Object} Read-only mode state
 * @returns {boolean} isReadOnly - Whether read-only mode is currently active
 * @returns {boolean} isLoading - Whether the read-only state is being checked
 * 
 * @example
 * // In a form or action component
 * const { isReadOnly, isLoading } = useReadOnlyMode();
 * 
 * if (isLoading) return <Spinner />;
 * 
 * if (isReadOnly) {
 *   return <Alert>System is in read-only mode</Alert>;
 * }
 * 
 * @example
 * // Conditionally disable buttons
 * const { isReadOnly } = useReadOnlyMode();
 * 
 * <Button disabled={isReadOnly}>
 *   Save Changes
 * </Button>
 */

import { useState, useEffect } from 'react';
import { featureFlags, FEATURE_FLAGS } from '@/lib/featureFlags';
import { useAuth } from '@/lib/auth';

export const useReadOnlyMode = () => {
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { user, organization } = useAuth();

  useEffect(() => {
    const checkReadOnlyMode = async () => {
      try {
        // Admin users bypass read-only mode
        if (user?.role === 'admin') {
          setIsReadOnly(false);
          setIsLoading(false);
          return;
        }

        const enabled = await featureFlags.isEnabled(
          FEATURE_FLAGS.READ_ONLY_MODE,
          user?.id,
          organization?.id
        );
        setIsReadOnly(enabled);
      } catch (error) {
        console.error('Error checking read-only mode:', error);
        setIsReadOnly(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkReadOnlyMode();
  }, [user, organization]);

  return { isReadOnly, isLoading };
};
